import React, { useEffect, useMemo, useRef, useState } from "react";
import { createRoot } from "react-dom/client";
import {
  AlertTriangle,
  CheckCircle2,
  Clipboard,
  Code2,
  Download,
  FileUp,
  Grid3X3,
  ImageDown,
  Languages,
  RefreshCw,
  ShieldCheck,
  SquareCode,
  ZoomIn,
  ZoomOut
} from "lucide-react";
import {
  buildMarkdownBlock,
  extractMermaidCode,
  mermaidVersionLabel,
  renderMermaid,
  svgSize,
  type DiagramCheck,
  type RenderResult,
  type SecurityLevel,
  type ThemePreset
} from "./diagramEngine";
import { getSample, sampleDiagrams, type SampleId } from "./sampleDiagrams";
import "./styles.css";

type Language = "en" | "ko";

type CopyStatus = {
  label: string;
  tone: "ok" | "warn";
};

const copy = {
  en: {
    subtitle: "Mermaid Publisher QA Studio",
    source: "SOURCE",
    sourceKo: "소스 코드",
    live: "LIVE RENDER",
    liveKo: "실시간 미리보기",
    qa: "PUBLISH QA",
    qaKo: "발행 전 검사",
    samples: "SAMPLE DIAGRAMS",
    markdown: "GITHUB MARKDOWN",
    markdownKo: "자동 생성",
    copyMarkdown: "Copy Markdown",
    copySource: "Copy Source",
    exportSvg: "Export SVG",
    exportPng: "Export PNG",
    open: "Open .mmd",
    refresh: "Refresh Checks",
    rendered: "Rendered",
    renderError: "Render error",
    noSvg: "No SVG to export yet.",
    theme: "Theme",
    security: "Security",
    strict: "Strict",
    diagramType: "Diagram type",
    metrics: "Diagram metrics",
    usage: "Usage tips",
    local: "All processing happens in your browser. Your diagram is not stored or sent anywhere.",
    ad: "Local rendering keeps diagram source outside any server workflow",
    about: "About",
    privacy: "Privacy",
    tips: [
      "GitHub README, Wiki, Issues, and PRs support Mermaid fenced blocks.",
      "Use accTitle and accDescr before publishing diagrams.",
      "SVG export keeps vector quality for docs and design handoff."
    ]
  },
  ko: {
    subtitle: "Mermaid 발행 QA 스튜디오",
    source: "소스",
    sourceKo: "Mermaid 코드",
    live: "실시간 렌더",
    liveKo: "브라우저 미리보기",
    qa: "발행 QA",
    qaKo: "문서 배포 전 점검",
    samples: "샘플 다이어그램",
    markdown: "GITHUB 마크다운",
    markdownKo: "자동 생성",
    copyMarkdown: "마크다운 복사",
    copySource: "소스 복사",
    exportSvg: "SVG 내보내기",
    exportPng: "PNG 내보내기",
    open: ".mmd 열기",
    refresh: "검사 새로고침",
    rendered: "렌더됨",
    renderError: "렌더 오류",
    noSvg: "아직 내보낼 SVG가 없습니다.",
    theme: "테마",
    security: "보안",
    strict: "Strict",
    diagramType: "다이어그램 타입",
    metrics: "다이어그램 지표",
    usage: "사용 팁",
    local: "모든 처리는 브라우저에서만 수행되며, 다이어그램 코드는 저장되거나 전송되지 않습니다.",
    ad: "다이어그램 소스는 이 브라우저에서만 렌더링됩니다",
    about: "소개",
    privacy: "개인정보",
    tips: [
      "GitHub README, Wiki, Issue, PR에서 Mermaid 코드 블록을 지원합니다.",
      "발행 전 accTitle과 accDescr을 추가하면 접근성이 좋아집니다.",
      "SVG 내보내기는 문서와 디자인 전달용 벡터 품질을 유지합니다."
    ]
  }
};

const themeOptions: { id: ThemePreset; label: string }[] = [
  { id: "neutral", label: "Neutral" },
  { id: "github", label: "GitHub" },
  { id: "print", label: "Print" },
  { id: "dark", label: "Dark" },
  { id: "forest", label: "Forest" },
  { id: "default", label: "Default" }
];

const securityOptions: SecurityLevel[] = ["strict", "antiscript", "loose"];

function readThemePreset(value: string): ThemePreset {
  switch (value) {
    case "neutral":
    case "github":
    case "print":
    case "dark":
    case "forest":
    case "default":
      return value;
    default:
      return "neutral";
  }
}

function readSecurityLevel(value: string): SecurityLevel {
  switch (value) {
    case "strict":
    case "antiscript":
    case "loose":
      return value;
    default:
      return "strict";
  }
}

function statusWord(status: DiagramCheck["status"], language: Language): string {
  if (status === "pass") return language === "ko" ? "통과" : "Pass";
  if (status === "warn") return language === "ko" ? "주의" : "Warn";
  return language === "ko" ? "실패" : "Fail";
}

function makeFileName(extension: string): string {
  const stamp = new Date().toISOString().slice(0, 19).replace(/[-:T]/g, "");
  return `diagramdock-${stamp}.${extension}`;
}

function downloadText(filename: string, content: string, type: string): void {
  const blob = new Blob([content], { type });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

async function copyToClipboard(value: string): Promise<void> {
  await navigator.clipboard.writeText(value);
}

async function svgToPngBlob(svg: string): Promise<Blob> {
  const { width, height } = svgSize(svg);
  const svgBlob = new Blob([svg], { type: "image/svg+xml;charset=utf-8" });
  const url = URL.createObjectURL(svgBlob);

  try {
    const image = new Image();
    image.decoding = "async";
    const loaded = new Promise<void>((resolve, reject) => {
      image.onload = () => resolve();
      image.onerror = () => reject(new Error("SVG image could not be loaded into canvas."));
    });
    image.src = url;
    await loaded;

    const canvas = document.createElement("canvas");
    canvas.width = width * 2;
    canvas.height = height * 2;
    const context = canvas.getContext("2d");
    if (!context) {
      throw new Error("Canvas rendering context is unavailable.");
    }
    context.fillStyle = "#f8faf6";
    context.fillRect(0, 0, canvas.width, canvas.height);
    context.scale(2, 2);
    context.drawImage(image, 0, 0, width, height);

    const blob = await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob((pngBlob) => {
        if (pngBlob) {
          resolve(pngBlob);
        } else {
          reject(new Error("PNG export failed."));
        }
      }, "image/png");
    });
    return blob;
  } finally {
    URL.revokeObjectURL(url);
  }
}

function App(): React.JSX.Element {
  const [language, setLanguage] = useState<Language>("en");
  const [sampleId, setSampleId] = useState<SampleId>("architecture");
  const [source, setSource] = useState(getSample("architecture").code);
  const [theme, setTheme] = useState<ThemePreset>("neutral");
  const [securityLevel, setSecurityLevel] = useState<SecurityLevel>("strict");
  const [result, setResult] = useState<RenderResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [copyStatus, setCopyStatus] = useState<CopyStatus | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const text = copy[language];
  const cleanCode = useMemo(() => extractMermaidCode(source), [source]);
  const markdownBlock = useMemo(() => buildMarkdownBlock(cleanCode), [cleanCode]);

  useEffect(() => {
    let active = true;
    setBusy(true);
    const handle = window.setTimeout(() => {
      renderMermaid(source, theme, securityLevel)
        .then((rendered) => {
          if (active) setResult(rendered);
        })
        .finally(() => {
          if (active) setBusy(false);
        });
    }, 160);

    return () => {
      active = false;
      window.clearTimeout(handle);
    };
  }, [source, theme, securityLevel]);

  useEffect(() => {
    if (!copyStatus) return;
    const timeout = window.setTimeout(() => setCopyStatus(null), 1800);
    return () => window.clearTimeout(timeout);
  }, [copyStatus]);

  const copyAction = async (label: string, value: string) => {
    try {
      await copyToClipboard(value);
      setCopyStatus({ label: language === "ko" ? `${label} 완료` : `${label} copied`, tone: "ok" });
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      setCopyStatus({ label: message, tone: "warn" });
    }
  };

  const downloadSvg = () => {
    if (!result?.svg) {
      setCopyStatus({ label: text.noSvg, tone: "warn" });
      return;
    }
    downloadText(makeFileName("svg"), result.svg, "image/svg+xml;charset=utf-8");
  };

  const downloadPng = async () => {
    if (!result?.svg) {
      setCopyStatus({ label: text.noSvg, tone: "warn" });
      return;
    }
    try {
      const blob = await svgToPngBlob(result.svg);
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = makeFileName("png");
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      setCopyStatus({ label: message, tone: "warn" });
    }
  };

  const openFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const content = await file.text();
    setSource(extractMermaidCode(content));
    event.target.value = "";
  };

  const selectSample = (id: SampleId) => {
    setSampleId(id);
    setSource(getSample(id).code);
    setZoom(1);
  };

  const checkGroups = useMemo(() => {
    const checks = result?.checks ?? [];
    return {
      syntax: checks.filter((check) => ["syntax", "render"].includes(check.id)),
      github: checks.filter((check) => ["github", "acctitle", "accdescr"].includes(check.id)),
      size: checks.filter((check) => check.id === "size")
    };
  }, [result]);

  return (
    <div className="app-shell">
      <header className="topbar">
        <div className="brand">
          <div className="brand-mark">
            <Grid3X3 size={26} />
          </div>
          <div>
            <h1>DiagramDock</h1>
            <p>{text.subtitle}</p>
          </div>
        </div>

        <div className="version-pill">
          <span />
          <strong>{mermaidVersionLabel}</strong>
          <small>renderer: local browser</small>
        </div>

        <div className="top-actions">
          <button className="lang-button" onClick={() => setLanguage(language === "en" ? "ko" : "en")}>
            <Languages size={18} />
            {language === "en" ? "EN | KO" : "KO | EN"}
          </button>
          <button onClick={() => copyAction(text.copyMarkdown, markdownBlock)}>
            <Clipboard size={18} />
            {text.copyMarkdown}
          </button>
          <button onClick={downloadSvg}>
            <Download size={18} />
            {text.exportSvg}
          </button>
          <button onClick={downloadPng}>
            <ImageDown size={18} />
            {text.exportPng}
          </button>
        </div>
      </header>

      {copyStatus ? <div className={`toast ${copyStatus.tone}`}>{copyStatus.label}</div> : null}

      <main className="workspace">
        <section className="source-pane panel">
          <div className="panel-heading">
            <div>
              <h2>{text.source}</h2>
              <p>{text.sourceKo}</p>
            </div>
            <div className="icon-row">
              <button aria-label={text.open} onClick={() => fileInputRef.current?.click()}>
                <FileUp size={18} />
              </button>
              <button aria-label={text.copySource} onClick={() => copyAction(text.copySource, cleanCode)}>
                <Clipboard size={18} />
              </button>
              <button aria-label={text.refresh} onClick={() => setSource((value) => `${value.trim()}\n`)}>
                <RefreshCw size={18} />
              </button>
            </div>
          </div>
          <input ref={fileInputRef} type="file" accept=".mmd,.md,.txt" onChange={openFile} />

          <div className="sample-strip" aria-label={text.samples}>
            <span>{text.samples}</span>
            <div>
              {sampleDiagrams.map((sample) => (
                <button
                  key={sample.id}
                  className={sampleId === sample.id ? "active" : ""}
                  onClick={() => selectSample(sample.id)}
                >
                  <strong>{sample.label}</strong>
                  <small>{sample.koLabel}</small>
                </button>
              ))}
            </div>
          </div>

          <textarea
            value={source}
            spellCheck={false}
            onChange={(event) => setSource(event.target.value)}
            aria-label="Mermaid source editor"
          />

          <div className="source-footer">
            <span>Ln {cleanCode.split(/\r?\n/).length}</span>
            <span>{cleanCode.length.toLocaleString()} chars</span>
            <span className={result?.error ? "status bad" : "status good"}>
              {result?.error ? text.renderError : busy ? "Checking" : "No errors"}
            </span>
          </div>
        </section>

        <section className="render-pane panel">
          <div className="panel-heading render-heading">
            <div>
              <h2>{text.live}</h2>
              <p>{text.liveKo}</p>
            </div>
            <div className="render-meta">
              <span>
                {text.diagramType}: <strong>{result?.diagramType ?? "..."}</strong>
              </span>
              <span className={result?.error ? "render-state bad" : "render-state good"}>
                {result?.error ? text.renderError : `${text.rendered} ${result?.renderedAt ?? ""}`}
              </span>
            </div>
          </div>

          <div className={`canvas-grid theme-${theme}`}>
            {result?.svg ? (
              <div
                className="diagram-frame"
                style={{ transform: `scale(${zoom})` }}
                dangerouslySetInnerHTML={{ __html: result.svg }}
              />
            ) : (
              <div className="error-slate">
                <AlertTriangle size={38} />
                <strong>{text.renderError}</strong>
                <p>{result?.error ?? "Waiting for Mermaid source."}</p>
              </div>
            )}
          </div>

          <div className="canvas-controls">
            <button aria-label="Zoom out diagram" onClick={() => setZoom((value) => Math.max(0.55, Number((value - 0.1).toFixed(2))))}>
              <ZoomOut size={18} />
            </button>
            <span>{Math.round(zoom * 100)}%</span>
            <button aria-label="Zoom in diagram" onClick={() => setZoom((value) => Math.min(1.8, Number((value + 0.1).toFixed(2))))}>
              <ZoomIn size={18} />
            </button>
          </div>
        </section>

        <aside className="qa-pane panel">
          <div className="panel-heading">
            <div>
              <h2>{text.qa}</h2>
              <p>{text.qaKo}</p>
            </div>
            <button onClick={() => setSource((value) => `${value.trim()}\n`)}>
              <RefreshCw size={18} />
              {text.refresh}
            </button>
          </div>

          <CheckSection title="Syntax & Render" checks={checkGroups.syntax} language={language} />
          <CheckSection title="GitHub Readiness" checks={checkGroups.github} language={language} />

          <div className="metrics-block">
            <h3>{text.metrics}</h3>
            <div className="metric-row">
              <span>Nodes</span>
              <strong>{result?.metrics.nodes ?? 0}</strong>
            </div>
            <div className="metric-row">
              <span>Edges</span>
              <strong>{result?.metrics.edges ?? 0}</strong>
            </div>
            <div className="metric-row">
              <span>Lines</span>
              <strong>{result?.metrics.lines ?? 0}</strong>
            </div>
          </div>

          <div className="settings-block">
            <label>
              <span>{text.theme}</span>
              <select value={theme} onChange={(event) => setTheme(readThemePreset(event.target.value))}>
                {themeOptions.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
            <label>
              <span>{text.security}</span>
              <select
                value={securityLevel}
                onChange={(event) => setSecurityLevel(readSecurityLevel(event.target.value))}
              >
                {securityOptions.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </label>
            <p>
              <ShieldCheck size={16} />
              {language === "ko" ? "기본값은 strict 보안 모드입니다." : "Default is strict security mode."}
            </p>
          </div>

          <div className="tips-block">
            <h3>{text.usage}</h3>
            {text.tips.map((tip) => (
              <p key={tip}>
                <CheckCircle2 size={15} />
                {tip}
              </p>
            ))}
          </div>
        </aside>

        <section className="markdown-pane panel">
          <div className="panel-heading">
            <div>
              <h2>{text.markdown}</h2>
              <p>{text.markdownKo}</p>
            </div>
            <button onClick={() => copyAction(text.copyMarkdown, markdownBlock)}>
              <Clipboard size={18} />
              {text.copyMarkdown}
            </button>
          </div>
          <pre>{markdownBlock}</pre>
        </section>
      </main>

      <footer className="footer">
        <div className="ad-slot">
          <Code2 size={18} />
          {text.ad}
        </div>
        <p>{text.local}</p>
        <nav>
          <a data-google-vignette="false" href="/about.html">{text.about}</a>
          <a data-google-vignette="false" href="/privacy.html">{text.privacy}</a>
          <a data-google-vignette="false" href="https://mermaid.js.org/" target="_blank" rel="noreferrer">
            Mermaid Docs
          </a>
        </nav>
      </footer>
    </div>
  );
}

function CheckSection({
  title,
  checks,
  language
}: {
  title: string;
  checks: DiagramCheck[];
  language: Language;
}): React.JSX.Element {
  return (
    <div className="check-section">
      <h3>{title}</h3>
      {checks.map((check) => (
        <div className={`check-row ${check.status}`} key={check.id}>
          {check.status === "pass" ? <CheckCircle2 size={20} /> : <AlertTriangle size={20} />}
          <div>
            <strong>{language === "ko" ? check.koLabel : check.label}</strong>
            <p>{language === "ko" ? check.koDetail : check.detail}</p>
          </div>
          <span>{statusWord(check.status, language)}</span>
        </div>
      ))}
    </div>
  );
}

const rootElement = document.getElementById("root");

if (!rootElement) {
  throw new Error("Root element not found.");
}

createRoot(rootElement).render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);
